import type { FC, Ref } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";
import { Checkbox } from "mds/components/Checkbox";
import { FormField } from "mds/components/FormField";
import { TextArea } from "mds/components/TextArea";
import { SquareButton } from "mds/components/buttons/SquareButton";

interface AltTextFormProps {
  className?: string;
  defaultValues?: FormValues;
  onSubmit: (data: FormValues) => void;
  onCancel?: () => void;
  ref?: Ref<HTMLFormElement>;
}

interface FormValues {
  text: string;
  isDecorative: boolean;
}

const AltTextForm: FC<AltTextFormProps> = ({
  className,
  defaultValues,
  onSubmit,
  onCancel,
  ref,
}) => {
  const form = useForm<FormValues>({
    defaultValues: defaultValues ?? {
      text: "",
      isDecorative: false,
    },
    mode: "onBlur",
  });
  const {
    register,
    watch,
    formState: { isDirty },
  } = form;
  const isDecorative = watch("isDecorative");

  return (
    <FormProvider {...form}>
      <form
        className={twMerge("flex flex-col gap-4", className)}
        ref={ref}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          disabled={isDecorative}
          helper={t("asset_field.alt_text_description")}
          label={t("asset_field.alt_text")}
          name="text"
        >
          {(controlProps) => (
            <TextArea
              {...register("text")}
              {...controlProps}
              minRows={3}
              placeholder={t("asset_field.alt_text_placeholder")}
            />
          )}
        </FormField>
        <Checkbox {...register("isDecorative")}>{t("asset_field.mark_as_decorative")}</Checkbox>

        <div className="mt-4 flex flex-row gap-2 self-end">
          <SquareButton kind="tertiary" size="m" onClick={onCancel}>
            {t("common.cancel")}
          </SquareButton>
          <SquareButton disabled={!isDirty} kind="accent" size="m" type="submit">
            {t("common.save")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};

// Using a default export to allow lazy loading
// eslint-disable-next-line import/no-default-export
export default AltTextForm;
